import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'

import Select from 'components/Form/Select/Select'
import UploadImage from 'components/Form/UploadImage/UploadImage'
import TextInput from 'components/Form/TextInput/TextInput'
import TextArea from 'components/Form/TextArea/TextArea'
import HiddenGroup from 'components/Form/HiddenGroup/HiddenGroup'
import ElementsGroup from 'components/Form/ElementsGroup/ElementsGroup'
import ImageSelector from 'components/Form/ImageSelector/ImageSelector'

import TextInputWithPageSelection from '../../../../components/TextInputWithPageSelection/TextInputWithPageSelection'

import ListActionBar from '../../../../Blocks/components/Controls/ListActionBar/ListActionBar'

import { RewardModel } from '../../RewardService'

import {
    REWARD_DATA,
    REWARD_AVAILABILITY_OPTIONS,
    REWARD_AVAILABILITY,
    MIN_REWARDS_COUNT,
    MAX_REWARDS_COUNT,
} from './constants'

import { getWinPercentage } from './utils'

import styles from './Reward.module.scss'

const Reward = ({
    errors,
    data,
    list,
    onHandleVisibility,
    index,
    weightFactorSum,
    onUpdate,
    onChangePosition,
    onClone,
    onRemove,
}) => {
    const { t } = useTranslation()

    const [weightOption, setWeightOption] = useState(null)
    const [weightOptions, setWeightOptions] = useState([])

    useEffect(() => {
        const selectedRewardData = REWARD_DATA[data[RewardModel.weight]]
        setWeightOption({
            value: selectedRewardData.value,
            label: selectedRewardData.getLabel(getWinPercentage(weightFactorSum, selectedRewardData.factor)),
        })
        setWeightOptions(
            Object.values(REWARD_DATA).map(reward => ({
                value: reward.value,
                label: reward.getLabel(
                    getWinPercentage(weightFactorSum - selectedRewardData.factor + reward.factor, reward.factor),
                ),
            })),
        )
    }, [weightFactorSum])

    return (
        <InView
            className={styles.section}
            as="li"
            onChange={onHandleVisibility}
            id={`item_${data[RewardModel.id]}`}
            threshold={0.1}
        >
            <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>
                    {t('Reward')} {index + 1}
                </h2>
                <ListActionBar
                    maxLength={MAX_REWARDS_COUNT}
                    minLength={MIN_REWARDS_COUNT}
                    index={index}
                    length={list.length}
                    onChangePosition={onChangePosition}
                    onClone={onClone}
                    onRemove={onRemove}
                />
            </div>
            <div className={styles.content}>
                <ImageSelector
                    label={t('Preview  ')}
                    imageUrl={data[RewardModel.previewImageUrl]}
                    onUpdate={v => onUpdate({ [RewardModel.previewImageUrl]: v })}
                />
                <div className={styles.mainContent}>
                    <div className={styles.flexBlock}>
                        <TextInput
                            className={styles.sectionNameControl}
                            label={t('Reward name')}
                            errorMessages={errors?.[RewardModel.name]}
                            isRequired
                            value={data[RewardModel.name]}
                            onUpdate={v => onUpdate({ [RewardModel.name]: v })}
                        />
                    </div>
                    <br />
                    <div className={styles.flexBlock}>
                        <Select
                            className={styles.prizeControlBox}
                            label={t('Probability')}
                            errorMessages={errors?.[RewardModel.weight]}
                            value={weightOption}
                            onUpdate={option => onUpdate({ [RewardModel.weight]: option.value })}
                            options={weightOptions}
                        />
                        <Select
                            className={styles.prizeControlBox}
                            label={t('Availability')}
                            errorMessages={errors?.[RewardModel.availability]}
                            value={REWARD_AVAILABILITY_OPTIONS.find(el => el.value === data[RewardModel.availability])}
                            onUpdate={option => onUpdate({ [RewardModel.availability]: option.value })}
                            options={REWARD_AVAILABILITY_OPTIONS}
                        />
                        {data[RewardModel.availability] === REWARD_AVAILABILITY.LIMITED && (
                            <TextInput
                                className={styles.prizeControlBox}
                                label={t('Quantity')}
                                isRequired
                                controlType="number"
                                errorMessages={errors?.[RewardModel.quantity]}
                                min={1}
                                value={data[RewardModel.quantity]}
                                onUpdate={v => onUpdate({ [RewardModel.quantity]: v })}
                            />
                        )}
                    </div>
                    <br />
                    <TextInput
                        label={t('Header')}
                        isRequired
                        errorMessages={errors?.[RewardModel.header]}
                        value={data[RewardModel.header]}
                        onUpdate={v => onUpdate({ [RewardModel.header]: v })}
                    />
                    <br />
                    <TextArea
                        label={t('Description')}
                        errorMessages={errors?.[RewardModel.description]}
                        value={data[RewardModel.description]}
                        onUpdate={v => onUpdate({ [RewardModel.description]: v })}
                    />
                    <br />
                    <UploadImage
                        isRequired
                        label={t('Image')}
                        isDisabledDelete
                        errorMessages={errors?.[RewardModel.imageUrl]}
                        value={data[RewardModel.imageUrl]}
                        onUpdate={v => onUpdate({ [RewardModel.imageUrl]: v })}
                    />
                    <HiddenGroup
                        defaultIsVisible={
                            data[RewardModel.imageDisclaimer] ||
                            data[RewardModel.buttonText] ||
                            data[RewardModel.buttonUrl]
                        }
                    >
                        {!!data[RewardModel.imageUrl] && (
                            <TextInput
                                label={t('Image disclaimer')}
                                isOptional
                                errorMessages={errors?.[RewardModel.imageDisclaimer]}
                                value={data[RewardModel.imageDisclaimer]}
                                onUpdate={v => onUpdate({ [RewardModel.imageDisclaimer]: v })}
                            />
                        )}
                        <ElementsGroup isEquallyWidth>
                            <TextInput
                                label={t('Button text')}
                                errorMessages={errors?.[RewardModel.buttonText]}
                                value={data[RewardModel.buttonText]}
                                onUpdate={v => onUpdate({ [RewardModel.buttonText]: v })}
                            />
                            <TextInputWithPageSelection
                                errorMessages={errors?.[RewardModel.buttonUrl]}
                                value={data[RewardModel.buttonUrl]}
                                onUpdate={v => onUpdate({ [RewardModel.buttonUrl]: v })}
                            />
                        </ElementsGroup>
                    </HiddenGroup>
                </div>
            </div>
        </InView>
    )
}

export default Reward
